/**
 * Promise-based semaphore for limiting concurrent async operations.
 *
 * When the number of in-flight operations reaches `maxConcurrent`, additional
 * calls to `run()` are queued (FIFO) and executed as slots free up.
 */
export class ConcurrencyLimiter {
  private running = 0;
  private queue: Array<() => void> = [];

  constructor(private maxConcurrent: number) {}

  run<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const execute = () => {
        this.running++;
        Promise.resolve()
          .then(fn)
          .then(resolve, reject)
          .finally(() => {
            this.running--;
            const next = this.queue.shift();
            if (next) next();
          });
      };

      if (this.running < this.maxConcurrent) {
        execute();
      } else {
        this.queue.push(execute);
      }
    });
  }

  // ── Test-only exports ────────────────────────────────────────────────

  _getRunningCount(): number {
    return this.running;
  }

  _getQueueLength(): number {
    return this.queue.length;
  }
}
